import { ReactElement, FC, useEffect, useState, useCallback, useMemo } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/auth'
import { useApplication } from '../context/application'
import { useCustomer } from '../context/customer'
import { useRenewal } from '../context/renewal'
import ApplicationMainLayout from '../components/application/MainLayout'
import PortalMainLayout from '../components/portal/MainLayout'
import { Wrapper } from '@googlemaps/react-wrapper'
import { appconfig } from 'src/config'
import FirmPracticePage from '../pages/NewApplications/Firm/Practice'
import { useNavigate } from 'react-router-dom'
import id from 'date-fns/esm/locale/id/index.js'

interface AppRouteProps {
  component: ReactElement;
  redirectTo?: string;
  mainLayout?: 'application' | 'portal'
  isPrivate?: boolean;
  gmaps?: boolean;
}

const AppRoute: FC<AppRouteProps> = ({ component, redirectTo, mainLayout, isPrivate, gmaps = false }) => {
  const { pathname, search } = useLocation()
  const { updateApplication, getApplication, application } = useApplication()
  const { validateCookies, user } = useAuth()
  const { getRenewalData, renewalData } = useRenewal()
  const { getFirmDetails, firmData } = useCustomer()
  const [willRender, setWillRender] = useState<{ render: boolean, component?: ReactElement }>({ render: false, component: undefined })
  const navigate = useNavigate()

  const ComponentWithMainLayout = useCallback(() => {
    const googleApiKey = appconfig.GOOGLE_API_KEY
    
    const componentWithMap = gmaps && googleApiKey ? (
      <Wrapper
        apiKey={googleApiKey}
        language='en'
        libraries={['places']}
      >
        {component}
      </Wrapper>
    ) : component
    
    switch(mainLayout) {
      case 'application':
        return <ApplicationMainLayout>{componentWithMap}</ApplicationMainLayout>
      case 'portal':
        return <PortalMainLayout>{componentWithMap}</PortalMainLayout>
      default:
        return componentWithMap
    }
  }, [component, gmaps, mainLayout])

  const validateRoleAccess = useCallback((role: string) => {
    switch(role) {
      case 'Admin':
        return pathname.includes('/admin')
      case 'Client':
        return pathname.includes('/customer')
      case 'Applicant':
        return !pathname.includes('/admin') && !pathname.includes('/customer')
      default: return false
    }
  }, [pathname])

  const unauthenticatedRoute = `/unauthenticated?redirect=${pathname}${search}`

  const handlePageStartWithoutUser = useCallback(async () => {
    const cookies = document.cookie

    if(cookies.includes('Cookies')) {
      const userData = await validateCookies()

      if(!userData) {
        if(isPrivate) {
          return setWillRender({ render: true, component: <Navigate to={redirectTo ?? unauthenticatedRoute} /> })
        } else {
          return setWillRender({ render: true, component: <ComponentWithMainLayout /> })
        }
      }

      const canUserAccess = validateRoleAccess(userData.role)

      const renderObject = { render: true,
        component: canUserAccess ? 
          <ComponentWithMainLayout /> : 
          (isPrivate ? <Navigate to={redirectTo ?? unauthenticatedRoute} /> : <ComponentWithMainLayout />)
      }

      if(userData.role === 'Client') {
        await Promise.all([
          !firmData?.number && getFirmDetails(userData.firmId)//,
          //(pathname.includes('/renew') && !renewalData?.id) //&& getRenewalData()
        ])

        if((pathname.includes('/renew') && !renewalData?.id)){
          let getrenewalResult = await getRenewalData();
          if(getrenewalResult === "/customer/renew/firm/practice"){
            console.log(`<<<<<< arrrive renewal redirect ${getrenewalResult}`)
            redirectTo = getrenewalResult;
            navigate(getrenewalResult)
            //return;
          }
        }

        return setWillRender(renderObject)
      }

      if ( userData.role === 'Applicant' && !canUserAccess && isPrivate) {
        //the user still is an applicant from their application submitted cookie,
        //but now they've clicked on /customer/invoices or something they don't have access to.
        //so the render object is the Navigate from above, 
        // we want to do that instead of continue and try to getApplication()
        return setWillRender({ render: true, component: <Navigate to={redirectTo ?? unauthenticatedRoute} /> }); 
      }

      if(userData.role !== 'Applicant') {
        return setWillRender(renderObject)
      }

      updateApplication({ applicant: {
        applicantId: userData?.id,
        emailAddress: userData?.email,
        isFirmAdministrator: userData?.isFirmAdministrator,
        firstName: userData?.firstName,
        lastName: userData?.lastName
      }})

      var getapplicationResult =  await getApplication()

      if(getapplicationResult === "/firm/practice"){
        redirectTo = getapplicationResult;
        navigate(getapplicationResult)
      }

      return setWillRender({ ...renderObject })
    } else {
      if(isPrivate) {
        setWillRender({ render: true, component: <Navigate to={redirectTo ?? unauthenticatedRoute} /> })
      } else {
        setWillRender({ render: true, component: <ComponentWithMainLayout /> })
      }
    }
  }, [ComponentWithMainLayout, firmData?.number, getApplication, getFirmDetails, getRenewalData, isPrivate, pathname, redirectTo, renewalData?.id, unauthenticatedRoute, updateApplication, validateCookies, validateRoleAccess])

  const handlePageStartWithUser = useCallback(async () => {
    const canUserAccess = validateRoleAccess(user.role)

    if(user.role === 'Client') {
      await Promise.all([
        !firmData?.number && getFirmDetails(user.firmId),
        (pathname.includes('/renew') && !renewalData?.id) && getRenewalData()
      ])
    }

    return setWillRender({ 
      render: true, component: canUserAccess ? 
      <ComponentWithMainLayout /> : 
      (isPrivate ? <Navigate to={redirectTo ?? unauthenticatedRoute} /> : <ComponentWithMainLayout />)
    })
  }, [ComponentWithMainLayout, firmData?.number, getFirmDetails, getRenewalData, isPrivate, pathname, redirectTo, renewalData?.id, unauthenticatedRoute, user.firmId, user.role, validateRoleAccess])
  
  useEffect(() => {
    if(isPrivate === true) {
      if(user && user?.role) {
        handlePageStartWithUser()
      } else {
        handlePageStartWithoutUser()
      }
    } else {
      setWillRender({ render: true, component: <ComponentWithMainLayout /> })
    }
  }, [pathname])

  const validateWillRender = useMemo(() => {
    if(willRender.render === true && !!willRender.component) {
      if(!isPrivate) return true
      if(user && user.role !== 'Applicant') return true
      //we are an applicant from here down...
      const canUserAccess = validateRoleAccess(user.role);
      const isNavToUnauthComp = !!willRender.component.props?.to?.match(/unauthenticated/);
      if (user && user.role === 'Applicant' && !canUserAccess && isPrivate && isNavToUnauthComp) return true;
      if(!!application?.id) return true
    }
    return false
  }, [application?.id, isPrivate, user, willRender.component, willRender.render,validateRoleAccess])

  return validateWillRender ? 
    willRender.component as ReactElement : 
    null
}

export default AppRoute